/**
 * Custom hook for managing focus group session state
 */
import { useState, useRef, useCallback, useEffect } from 'react';
import { saveSessionRecovery, getSessionRecovery, clearSessionRecovery } from '../utils/storage';
import { useTranslation } from '../i18n/LanguageContext';

const API_BASE = import.meta.env.VITE_API_URL || 'http://localhost:8000';

export function useSession() {
  const { t, language } = useTranslation();
  const [stage, setStage] = useState('setup'); // setup, running, guidance, report
  const [messages, setMessages] = useState([]);
  const [currentRound, setCurrentRound] = useState(0);
  const [currentSpeaker, setCurrentSpeaker] = useState(null);
  const [streamingContent, setStreamingContent] = useState('');
  const [roundSummaries, setRoundSummaries] = useState([]);
  const [finalAnalysis, setFinalAnalysis] = useState(null);
  const [moderatorSummary, setModeratorSummary] = useState('');
  const [sessionId, setSessionId] = useState(null);
  const [isGeneratingReport, setIsGeneratingReport] = useState(false);
  const [error, setError] = useState(null);
  const [isPaused, setIsPaused] = useState(false);
  const [rateLimitInfo, setRateLimitInfo] = useState(null);
  const [reportGuidance, setReportGuidance] = useState('');
  const [beliefStates, setBeliefStates] = useState({});
  const [topicDimensions, setTopicDimensions] = useState([]);
  const [groupDynamics, setGroupDynamics] = useState([]);
  const [personaTrajectories, setPersonaTrajectories] = useState({});
  const [operatorGuidance, setOperatorGuidance] = useState('');

  // Refs for streaming
  const streamingContentRef = useRef('');
  const currentSpeakerRef = useRef(null);
  const messagesEndRef = useRef(null);
  const abortControllerRef = useRef(null);

  // Auto-scroll to bottom of messages
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, streamingContent]);

  // Save session recovery data periodically
  useEffect(() => {
    if ((stage === 'running' || stage === 'guidance') && messages.length > 0) {
      const recoveryData = {
        sessionId,
        messages,
        roundSummaries,
        currentRound,
        moderatorSummary,
        stage,
      };
      saveSessionRecovery(recoveryData);
    }
  }, [messages, roundSummaries, currentRound, sessionId, stage, moderatorSummary]);

  // Check for session recovery on mount
  const checkForRecovery = useCallback(() => {
    const recovery = getSessionRecovery();
    if (recovery && recovery.messages?.length > 0) {
      return recovery;
    }
    return null;
  }, []);

  // Restore session from recovery data
  const restoreSession = useCallback((recovery) => {
    setMessages(recovery.messages || []);
    setRoundSummaries(recovery.roundSummaries || []);
    setCurrentRound(recovery.currentRound || 0);
    setSessionId(recovery.sessionId);
    if (recovery.moderatorSummary) {
      setModeratorSummary(recovery.moderatorSummary);
    }
    setStage('report'); // Go to report stage with partial data
    clearSessionRecovery();
  }, []);

  // Handle stream events
  const handleStreamEvent = useCallback((event) => {
    console.log('Event:', event.type, event);

    switch (event.type) {
      case 'session_start':
        setSessionId(event.session_id);
        break;

      case 'round_start':
        setCurrentRound(event.round);
        break;

      case 'speaker_start': {
        const speaker = {
          name: event.speaker,
          id: event.speaker_id,
          round: event.round,
          model: event.model,
        };
        currentSpeakerRef.current = speaker;
        streamingContentRef.current = '';
        setCurrentSpeaker(speaker);
        setStreamingContent('');
        break;
      }

      case 'content_chunk':
        streamingContentRef.current += event.chunk;
        setStreamingContent(streamingContentRef.current);
        break;

      case 'speaker_end': {
        const finalContent = streamingContentRef.current;
        const speakerInfo = currentSpeakerRef.current;

        if (finalContent && finalContent.trim()) {
          const newMessage = {
            id: `msg-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
            speaker: speakerInfo?.name || event.speaker,
            speakerId: speakerInfo?.id || 'unknown',
            content: finalContent,
            round: speakerInfo?.round || 0,
            model: speakerInfo?.model,
          };

          setMessages(prev => [...prev, newMessage]);
        }

        streamingContentRef.current = '';
        currentSpeakerRef.current = null;
        setStreamingContent('');
        setCurrentSpeaker(null);
        break;
      }

      case 'message_analyzed':
        setMessages(prev => {
          if (prev.length === 0) return prev;
          const updated = [...prev];
          updated[updated.length - 1] = {
            ...updated[updated.length - 1],
            sentiment: event.sentiment,
            keyPoints: event.key_points,
          };
          return updated;
        });
        break;

      case 'belief_update':
        setBeliefStates(prev => ({
          ...prev,
          [event.speaker_id]: [...(prev[event.speaker_id] || []), event.belief_state],
        }));
        break;

      case 'dimension_update':
        setTopicDimensions(event.dimensions || []);
        break;

      case 'group_dynamics':
        setGroupDynamics(prev => [...prev, event.dynamics]);
        if (event.dynamics?.persona_trajectories) {
          setPersonaTrajectories(event.dynamics.persona_trajectories);
        }
        break;

      case 'round_summary':
        setRoundSummaries(prev => [...prev, event.summary]);
        break;

      case 'moderator_summary_start':
        setModeratorSummary('');
        break;

      case 'moderator_summary_chunk':
        setModeratorSummary(prev => prev + event.chunk);
        break;

      case 'moderator_summary_end':
        break;

      case 'discussion_complete':
        setStage('guidance');
        break;

      case 'generating_report':
        setIsGeneratingReport(true);
        break;

      case 'final_analysis':
        setFinalAnalysis(event.analysis);
        setIsGeneratingReport(false);
        break;

      case 'session_complete':
        setStage('report');
        setIsGeneratingReport(false);
        clearSessionRecovery();
        break;

      case 'rate_limit':
        setRateLimitInfo({
          provider: event.provider,
          retryAfter: event.retry_after,
          message: event.message,
        });
        setTimeout(() => setRateLimitInfo(null), event.retry_after * 1000);
        break;

      case 'error':
        setError(event.message);
        break;

      default:
        console.log('Unknown event type:', event.type);
    }
  }, []);

  // Start session
  const startSession = useCallback(async (config, personas) => {
    if (!config.topic || personas.length === 0) return;

    setStage('running');
    setMessages([]);
    setRoundSummaries([]);
    setCurrentRound(0);
    setError(null);
    setModeratorSummary('');
    setIsPaused(false);
    streamingContentRef.current = '';
    currentSpeakerRef.current = null;
    setStreamingContent('');
    setCurrentSpeaker(null);

    // Create abort controller for pause/cancel
    abortControllerRef.current = new AbortController();

    const validQuestions = config.questions.filter(q => q.trim());

    try {
      const response = await fetch(`${API_BASE}/session/start`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          title: config.title,
          topic: config.topic,
          description: config.description,
          questions: validQuestions,
          moderator_briefing: config.moderatorBriefing,
          moderator_direction: config.moderatorDirection,
          moderator_avoid: config.moderatorAvoid,
          personas: personas,
          rounds: config.rounds,
          moderator_model: config.moderatorModel,
          language: language,
          devils_advocate: config.devilsAdvocate || false,
          enhanced_belief_tracking: config.enhancedBeliefTracking || false,
        }),
        signal: abortControllerRef.current.signal,
      });

      if (!response.ok) throw new Error('Failed to start session');

      const sessionIdHeader = response.headers.get('X-Session-ID');
      setSessionId(sessionIdHeader);

      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let buffer = '';

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split('\n\n');
        buffer = lines.pop() || '';

        for (const line of lines) {
          if (line.startsWith('data: ')) {
            try {
              const event = JSON.parse(line.slice(6));
              handleStreamEvent(event);
            } catch (e) {
              console.error('Failed to parse event:', e);
            }
          }
        }
      }
    } catch (err) {
      if (err.name === 'AbortError') {
        console.log('Session was paused/cancelled');
        return;
      }
      setError(t('errors.sessionError'));
      console.error(err);
      setStage('setup');
    }
  }, [handleStreamEvent, language, t]);

  // Pause session
  const pauseSession = useCallback(async () => {
    if (!sessionId || !abortControllerRef.current) return;
    try {
      await fetch(`${API_BASE}/session/${sessionId}/pause`, { method: 'POST' });
    } catch (err) {
      console.error('Failed to pause on backend:', err);
    }
    abortControllerRef.current.abort();
    setIsPaused(true);
  }, [sessionId]);

  // Resume session
  const resumeSession = useCallback(async () => {
    if (!sessionId) return;

    // Create a new AbortController for the resumed stream
    abortControllerRef.current = new AbortController();
    setIsPaused(false);

    try {
      const response = await fetch(`${API_BASE}/session/${sessionId}/resume`, {
        method: 'POST',
        signal: abortControllerRef.current.signal,
      });

      if (!response.ok) throw new Error('Failed to resume session');

      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let buffer = '';

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split('\n\n');
        buffer = lines.pop() || '';

        for (const line of lines) {
          if (line.startsWith('data: ')) {
            try {
              const event = JSON.parse(line.slice(6));
              handleStreamEvent(event);
            } catch (e) {
              console.error('Failed to parse event:', e);
            }
          }
        }
      }
    } catch (err) {
      if (err.name === 'AbortError') {
        console.log('Resumed session was paused/cancelled');
        return;
      }
      setError(t('errors.sessionError'));
      console.error(err);
    }
  }, [sessionId, handleStreamEvent, t]);

  // Send operator guidance to steer moderator
  const sendOperatorGuidance = useCallback(async (text) => {
    if (!sessionId || !text.trim()) return;
    try {
      const response = await fetch(`${API_BASE}/session/${sessionId}/guidance`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ text: text.trim() }),
      });
      if (!response.ok) throw new Error('Failed to send guidance');
      setOperatorGuidance('');
    } catch (err) {
      console.error('Failed to send operator guidance:', err);
      setError(t('errors.sessionError'));
    }
  }, [sessionId, t]);

  // Generate report (called from guidance stage)
  const generateReport = useCallback(async (guidance = '') => {
    if (!sessionId) return;

    setReportGuidance(guidance);
    setStage('running');
    setIsGeneratingReport(true);
    setError(null);

    try {
      const response = await fetch(`${API_BASE}/session/${sessionId}/generate-report`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ guidance }),
      });

      if (!response.ok) throw new Error('Failed to generate report');

      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let buffer = '';

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split('\n\n');
        buffer = lines.pop() || '';

        for (const line of lines) {
          if (line.startsWith('data: ')) {
            try {
              const event = JSON.parse(line.slice(6));
              handleStreamEvent(event);
            } catch (e) {
              console.error('Failed to parse event:', e);
            }
          }
        }
      }
    } catch (err) {
      setError(t('errors.reportGenerationError'));
      console.error(err);
      setStage('guidance'); // Go back to guidance on error
      setIsGeneratingReport(false);
    }
  }, [sessionId, handleStreamEvent, t]);

  // Reset session
  const resetSession = useCallback(() => {
    setStage('setup');
    setMessages([]);
    setRoundSummaries([]);
    setFinalAnalysis(null);
    setModeratorSummary('');
    setCurrentRound(0);
    setSessionId(null);
    setError(null);
    setIsPaused(false);
    setReportGuidance('');
    setOperatorGuidance('');
    setBeliefStates({});
    setTopicDimensions([]);
    setGroupDynamics([]);
    setPersonaTrajectories({});
    clearSessionRecovery();
  }, []);

  // Export functions
  const exportPDF = useCallback(async () => {
    if (!sessionId) return;

    try {
      const response = await fetch(`${API_BASE}/report/${sessionId}/pdf`);
      if (!response.ok) throw new Error('Failed to export PDF');

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${t('export.pdfFilename')}_${sessionId}.pdf`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (err) {
      setError(t('errors.pdfExport'));
      console.error(err);
    }
  }, [sessionId]);

  const exportDOCX = useCallback(async () => {
    if (!sessionId) return;

    try {
      const response = await fetch(`${API_BASE}/report/${sessionId}/docx`);
      if (response.status === 501) {
        setError(t('errors.docxUnavailable'));
        return;
      }
      if (!response.ok) throw new Error('Failed to export DOCX');

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${t('export.docxFilename')}_${sessionId}.docx`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (err) {
      setError(t('errors.docxExport'));
      console.error(err);
    }
  }, [sessionId]);

  const exportAudio = useCallback(async () => {
    if (!sessionId) return;

    try {
      // Fetch estimate first
      const estResponse = await fetch(`${API_BASE}/report/${sessionId}/audio/estimate`);
      if (!estResponse.ok) throw new Error('Failed to get audio estimate');
      const estimate = await estResponse.json();

      const confirmed = window.confirm(
        t('export.audioConfirm')
          .replace('{characters}', estimate.total_characters.toLocaleString())
          .replace('{cost}', estimate.estimated_cost_usd.toFixed(2))
          .replace('{duration}', estimate.estimated_duration_minutes)
      );
      if (!confirmed) return;

      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 600000); // 10 min timeout

      const response = await fetch(`${API_BASE}/report/${sessionId}/audio`, {
        signal: controller.signal,
      });
      clearTimeout(timeoutId);

      if (response.status === 501) {
        setError(t('errors.audioUnavailable'));
        return;
      }
      if (!response.ok) throw new Error('Failed to export audio');

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${t('export.audioFilename')}_${sessionId}.mp3`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (err) {
      if (err.name === 'AbortError') {
        setError(t('errors.audioExport'));
      } else {
        setError(t('errors.audioExport'));
      }
      console.error(err);
    }
  }, [sessionId]);

  return {
    // State
    stage,
    setStage,
    messages,
    currentRound,
    currentSpeaker,
    streamingContent,
    roundSummaries,
    finalAnalysis,
    moderatorSummary,
    sessionId,
    isGeneratingReport,
    error,
    setError,
    isPaused,
    rateLimitInfo,
    reportGuidance,
    operatorGuidance,
    setOperatorGuidance,
    beliefStates,
    topicDimensions,
    groupDynamics,
    personaTrajectories,
    messagesEndRef,

    // Actions
    startSession,
    pauseSession,
    resumeSession,
    sendOperatorGuidance,
    resetSession,
    generateReport,
    exportPDF,
    exportDOCX,
    exportAudio,
    checkForRecovery,
    restoreSession,
  };
}
